import type { Site } from "@blog/content-schema";
import { useCallback, useEffect, useRef, useState } from "react";

import { describeError } from "./api-error";
import type { CmsApiClient } from "./api";

/**
 * Data-loading hooks.
 *
 * Deliberately minimal — no cache, no dedupe, no background refetch. The Admin
 * is a handful of pages over a low-traffic API, and a data library would be
 * more machinery than the problem has.
 */

export interface AsyncState<T> {
  readonly data: T | null;
  readonly error: string | null;
  readonly loading: boolean;
  /** Re-run the loader. */
  readonly reload: () => void;
}

/**
 * Run `loader` on mount and whenever `deps` change.
 *
 * The `cancelled` flag matters: a component that unmounts mid-request would
 * otherwise call `setState` on a dead component, and React will warn about it
 * in development and leak the promise in production.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // The loader is usually an inline arrow, so it changes identity every render.
  // Holding it in a ref keeps the effect from re-running on every parent render.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    loaderRef
      .current()
      .then((value) => {
        if (!cancelled) {
          setData(value);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(describeError(cause));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [...deps, nonce]);

  const reload = useCallback(() => {
    setNonce((value) => value + 1);
  }, []);

  return { data, error, loading, reload };
}

/** The site the Admin is working on. */
export interface SiteState {
  readonly site: Site | null;
  readonly error: string | null;
  readonly loading: boolean;
  readonly reload: () => void;
}

/**
 * Resolve the active site.
 *
 * MVP ships one site and the UI hides a selector (SPEC §7), but this reads the
 * list rather than assuming a fixed id — so the selector is a UI change, not a
 * data change.
 */
export function useSite(client: CmsApiClient): SiteState {
  const state = useAsync(async () => {
    const sites = await client.listSites();

    return sites[0] ?? null;
  }, [client]);

  return { site: state.data, error: state.error, loading: state.loading, reload: state.reload };
}
